import React, { useEffect, useState } from 'react';
import Card from 'components/Layout/Card';
import Profile from 'components/Layout/Profile';
import {
  getCountryCode,
  getDataFromLocalStorage,
  getFormDataArrayObject,
  objectToFormData,
  getUserType,
  paymentColor,
} from 'utils/helpers';
import moment from 'moment';
import TextArea from 'components/form/TextArea';
import Button from 'components/form/Button';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSwitchUserDetails,
  handleSendRBACRequestOTP,
  showSuccess,
  throwError,
  handleRBACVerifyOtpRequest,
} from 'store/slices';
import VerifyOtpModel from 'pages/Common/RequestRaisedAndReceived/Common/VerifyOtpModel';
import Loader from 'components/Layout/Loader';

const ViewSwitchUser = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const memberType = getUserType();
  const [otpData, setOtpData] = useState({});
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRejecting, setIsRejecting] = useState(false);
  const [switchUserDetails, setSwitchUserDetails] = useState({});

  const userRole = getDataFromLocalStorage('user_type');
  const { userDetails } = useSelector((state) => ({
    userDetails: state.student.userDetails,
  }));

  const { create: createAccess } =
    userDetails?.role_data?.find((item) => item?.permission_name === 'Requests')
      ?.sub_permission || {};

  const getSwitchUserDetails = async () => {
    const payload = {
      request_id: params?.request_id,
    };
    const response = await dispatch(fetchSwitchUserDetails(payload));
    if (response?.status === 200) {
      setSwitchUserDetails(response?.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getSwitchUserDetails();
  }, []);

  const {
    name,
    email_id,
    date_of_joining,
    current_role,
    profile_image,
    phone_number,
    country_code,
    member_id,
    switch_to,
    reqeust_status,
    verified_by,
    requested_by,
  } = switchUserDetails;

  const infoItems = [
    {
      label: 'Date of Joining',
      value: moment(date_of_joining).format('DD-MMM-YYYY'),
    },
    { label: 'Employee ID', value: member_id },
    { label: 'Current Role', value: current_role },
    { label: 'Switch To', value: switch_to },
    { label: 'Status', value: reqeust_status },
    ...(userRole === '1'
      ? [
          { label: 'Requested By', value: requested_by },
          { label: 'Approved By', value: verified_by },
        ]
      : [{ label: 'Approved By', value: verified_by }]),
  ];

  const initialValues = {
    comment: '',
  };

  const validationSchema = Yup.object({
    comment: Yup.string().required('Comment is required'),
  });

  const handleApproveRequest = async (values) => {
    setOtpData({
      comment: values?.comment,
      action: '1', // Approve
    });
    await sendOtpRequest();
  };

  const handleReject = async (values, setErrors) => {
    setIsRejecting(true);
    const errors = {};

    if (!values.comment.trim()) {
      errors.comment = 'Comment is required for rejection';
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '2',
    });

    await sendOtpRequest();
  };

  const sendOtpRequest = async () => {
    setIsLoading(true);
    const payload = objectToFormData({
      request_id: params?.request_id,
      type: 'SwitchUser',
    });

    try {
      const response = await dispatch(handleSendRBACRequestOTP(payload));
      setShowOtpDialog(true);
      if (response.status === 200) {
        dispatch(showSuccess(response.message));
      }
    } catch (error) {
      dispatch(throwError({ message: 'Failed to process request' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpVerification(true);
    const payload = getFormDataArrayObject({
      request_id: params?.request_id,
      otp: otp,
      comment: otpData?.comment,
      status: otpData?.action,
      type: 'SwitchUser',
    });
    try {
      const response = await dispatch(handleRBACVerifyOtpRequest(payload));
      if (response.status === 200) {
        navigate(`/${memberType}/request-received/switch-user`);
        setShowOtpDialog(false);
        dispatch(showSuccess(response?.message));
      }
    } catch (error) {
      console.error('Verification failed:', error);
    } finally {
      setOtpVerification(false);
    }
  };

  return (
    <>
      <Card className="br-10">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '300px' }}
          >
            <Loader size="sm" />
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center gap-3 cps-20 cpt-20">
              <i
                className="bi bi-chevron-left pointer"
                onClick={() => {
                  navigate(-1);
                }}
              />
              <div>View Switch User Details</div>
            </div>
            <div className="cps-80 cpt-60 cpe-80 cpb-80">
              <div className="d-flex gap-5 align-items-center">
                <Profile
                  text={name}
                  size="s-125"
                  url={profile_image}
                  isS3UserURL
                />
                <div className="d-flex flex-column">
                  <div className="text-20-400-25 color-new-car cmb-14">
                    {name}
                  </div>

                  <div className="d-flex align-items-center gap-4 cmb-20">
                    {email_id && (
                      <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2">
                        <i className="bi bi-envelope" /> {email_id}
                      </div>
                    )}
                    {phone_number && (
                      <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2">
                        <i className="bi bi-telephone" />
                        {`${getCountryCode(country_code)} ${phone_number}`}
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-column flex-md-row gap-md-4 flex-wrap cmb-10">
                    {infoItems.map(({ label, value }) => (
                      <div key={label}>
                        <h6 className="text-14-400-18">{label}</h6>
                        <p
                          className={
                            label.toLowerCase() === 'status'
                              ? paymentColor(value)
                              : 'text-14-300-18'
                          }
                        >
                          {value ? value : '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {userRole === '1' && reqeust_status === 'Pending' && (
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setErrors }) => {
                    if (isRejecting) {
                      handleReject(values, setErrors);
                    } else {
                      handleApproveRequest(values);
                    }
                  }}
                >
                  {(props) => {
                    const {
                      values,
                      errors,
                      handleChange,
                      handleSubmit,
                      setErrors,
                    } = props;

                    return (
                      <form onSubmit={handleSubmit}>
                        <div className="row d-flex justify-content-between align-items-center cmb-26">
                          <div className="col-md-8 cmb-24">
                            <div className="cmt-10">
                              <TextArea
                                rows={3}
                                label="Comment"
                                name="comment"
                                value={values.comment}
                                onChange={handleChange}
                                error={errors.comment}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex gap-3 cpt-19">
                          {createAccess === '1' && (
                            <>
                              <Button
                                text="Approve Request"
                                btnStyle="primary-dark"
                                className="h-37 text-14-500 cps-20 cpe-20"
                                onClick={() => handleApproveRequest(values)}
                                disabled={isLoading}
                              />
                              <Button
                                text="Reject"
                                btnStyle="primary-light-outline"
                                className="h-37 text-14-500 cps-20 cpe-20"
                                onClick={() => handleReject(values, setErrors)}
                                disabled={isLoading}
                              />
                            </>
                          )}
                          <Button
                            text="Cancel"
                            btnStyle="light-outline"
                            className="h-37 text-14-500 cps-20 cpe-20"
                            disabled={isLoading}
                            onClick={() => navigate(-1)}
                          />
                        </div>
                      </form>
                    );
                  }}
                </Formik>
              )}
            </div>
          </>
        )}
      </Card>
      {showOtpDialog && (
        <VerifyOtpModel
          title="Switch User Request"
          showApproveDialog={showOtpDialog}
          onHide={() => setShowOtpDialog(false)}
          onVerify={handleVerifyOtp}
          sendOtpRequest={sendOtpRequest}
          otpVerification={otpVerification}
        />
      )}
    </>
  );
};

export default ViewSwitchUser;
